import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MdClose } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";

import { useRestaurantNav } from "../../hooks/useRestaurantNav";
import {
  createCampaignRequest,
  resetCampaignCreation,
} from "../../redux/slices/campaignCreationSlice";
import type { RootState } from "../../redux/store";
import CreateOwnForm from "../editCampaign/CreateOwnForm";

interface CreateCampaignDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

const POST_YOUR_OWN_CATEGORY = "post_your_own";

const CreateCampaignDrawer: React.FC<CreateCampaignDrawerProps> = ({
  isOpen,
  onClose,
}) => {
  const dispatch = useDispatch();
  const { navigate } = useRestaurantNav();

  const restaurantId = useSelector(
    (state: RootState) => state.clover.restaurantId,
  );

  const {
    loading: creationLoading,
    success: creationSuccess,
    error: creationError,
    response: creationResponse,
  } = useSelector((state: RootState) => state.campaignCreation);

  const [postYourOwn, setPostYourOwn] = useState(false);

  const handleClose = () => {
    setPostYourOwn(false);
    onClose();
  };

  useEffect(() => {
    if (!creationSuccess || creationLoading || !restaurantId) {
      return;
    }

    handleClose();

    const requestId =
      creationResponse?.requestId || creationResponse?.data?.requestId;

    const campaignId =
      creationResponse?.data?.id ||
      creationResponse?.id ||
      creationResponse?.campaignId ||
      creationResponse?.data?.campaignId;

    if (campaignId) {
      const navigationState: {
        campaignId: string;
        requestId?: string;
      } = {
        campaignId,
      };

      if (requestId) {
        navigationState.requestId = requestId;
      }

      navigate(`/${restaurantId}/campaign-generator`, {
        state: navigationState,
      });
    } else {
      navigate(`/${restaurantId}/flyer`);
    }

    dispatch(resetCampaignCreation());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    creationSuccess,
    creationLoading,
    navigate,
    dispatch,
    restaurantId,
    creationResponse,
  ]);

  useEffect(() => {
    if (creationError && !creationLoading) {
      console.error("Campaign creation error:", creationError);
    }
  }, [creationError, creationLoading]);

  const handleFormSubmit = (data: any) => {
    if (!restaurantId) {
      console.error("No restaurant ID found!");
      return;
    }

    const commonPayload = {
      type: "manual",
      title: data.title,
      description: data.title,
      offerDetails: data.offerInfo || data.offerDetails || "",
      tagLine: data.tagLine || "",
      contactInfo: data.contactPhone || data.contactInfo || "",
      validFrom: data.validFrom,
      validTo: data.validTo,
      themeColour: data.themeColors?.[0] || data.themeColour || "#EE4423",
      menuItems: data.menuItems || [],
      combo: data.combo || [],
      eventId: "",
      event: {},
    };

    const campaignPayload = postYourOwn
      ? {
          ...commonPayload,
          category: POST_YOUR_OWN_CATEGORY,
          flyerImage: data.flyerImage || data.refImage?.[0] || "",
        }
      : {
          ...commonPayload,
          instructions: data.Instructions || "",
          refImage: data.refImage || [],
          category: "",
          flyerImage: "",
        };

    dispatch(createCampaignRequest(campaignPayload));
  };

  const handleButtonClick = () => {
    const form = document.querySelector<HTMLFormElement>(
      "#create-campaign-form form",
    );

    form?.requestSubmit();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="fixed inset-0 z-40 bg-black/60"
          />

          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.3 }}
              className="flex h-[85vh] w-full max-w-7xl flex-col overflow-hidden rounded-2xl"
              style={{
                backdropFilter: "blur(10px)",
                boxShadow: "rgba(0,0,0,0.3) 0px 0px 30px 0px",
                background:
                  "linear-gradient(90deg,rgba(33,33,33,0.98),rgba(55,55,55,0.98))",
                border: "1px solid rgba(224,224,224,0.13)",
              }}
            >
              <div className="flex items-center justify-between px-6 py-4">
                <div className="flex items-center gap-6">
                  <h2 className="text-xl font-bold text-white">
                    Create Campaign
                  </h2>

                  <label className="flex cursor-pointer items-center gap-2 text-sm text-white">
                    <input
                      type="checkbox"
                      checked={postYourOwn}
                      onChange={(event) => setPostYourOwn(event.target.checked)}
                      className="h-4 w-4 cursor-pointer accent-[#EE4423]"
                    />
                    <span>Post Your Own</span>
                  </label>
                </div>

                <button
                  type="button"
                  onClick={handleClose}
                  className="cursor-pointer rounded-full p-2 transition-colors hover:bg-gray-700/50"
                  aria-label="Close create campaign"
                >
                  <MdClose size={24} className="text-white" />
                </button>
              </div>

              <div className="flex flex-1 flex-col overflow-y-auto px-6">
                <div id="create-campaign-form">
                  <CreateOwnForm
                    onSubmit={handleFormSubmit}
                    postYourOwn={postYourOwn}
                    isSubmitting={creationLoading}
                    submitLabel={
                      postYourOwn ? "Post Campaign" : "Generate Campaign"
                    }
                  />
                </div>
              </div>

              {!postYourOwn && (
                <div className="flex justify-end p-6">
                  <motion.button
                    type="button"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleButtonClick}
                    disabled={creationLoading}
                    className={`rounded-xl border px-8 py-2 text-sm font-medium transition-colors ${
                      creationLoading
                        ? "cursor-not-allowed border-gray-600 text-gray-400"
                        : "cursor-pointer border-brand-primary text-gray-400 hover:text-white"
                    }`}
                  >
                    {creationLoading
                      ? "Generating Now..."
                      : "Generate Campaign"}
                  </motion.button>
                </div>
              )}
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CreateCampaignDrawer;
